import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  FaPlus,
  FaTimes,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import "../styles/LayerToggle.css";
import LayerList from "./LayerList";
import InfoIcon from "./InfoIcon";
import LayerGroupIcon from "./LayerGroupIcon";
import LayerInfoModal from "./LayerInfoModal";

const CollapsibleCategory = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div className="collapsible-category">
      <div className="collapsible-header" onClick={toggleOpen}>
        <span className="collapsible-title">{title}</span>
        <span className="collapsible-arrow">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>
      {isOpen && (
        <div className="collapsible-body">
          {children}
        </div>
      )}
    </div>
  );
};

function groupByCategory(layers) {
  const grouped = {};
  layers.forEach((layer) => {
    const cat = layer.category || "Other";
    if (!grouped[cat]) {
      grouped[cat] = [];
    }
    grouped[cat].push(layer);
  });
  return grouped;
}

const LayerToggle = ({
  layersConfig = [],
  activeLayers = [],
  addLayer,
  removeLayer,
  resetLayers,
  forestLossRange,
  updateForestLossRange,
  updateBiomassYear,
  onToggleVisibility,
  onOpacityChange,
  onClose,
  deforestationRange,
  updateDeforestationRange,
  biomassYear,
  isOpen,
  isExpanded,
  onExpandChange,
}) => {
  const [activeTab, setActiveTab] = useState("active");
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(!isExpanded);
  const [collapseAnimation, setCollapseAnimation] = useState('');

  // Animation duration in ms (should match CSS transition duration)
  const ANIMATION_DURATION = 300;

  // Ref for tracking timeout IDs to clean them up
  const animationTimeoutRef = useRef(null);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  // Debounce function to limit rapid state updates
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Restore collapsed state from localStorage on mount
  useEffect(() => {
    const collapsed = localStorage.getItem("layerToggleCollapsed") === "true";
    setIsCollapsed(collapsed || !isExpanded);
    if (onExpandChange) {
      onExpandChange(!collapsed && isExpanded);
    }
  }, []);

  // Debounced handler for saving collapsed state and notifying parent
  const debouncedExpandChange = debounce((newCollapsed) => {
    localStorage.setItem("layerToggleCollapsed", newCollapsed);
    if (onExpandChange) {
      onExpandChange(!newCollapsed);
    }
  }, 100);

  useEffect(() => {
    debouncedExpandChange(isCollapsed);
  }, [isCollapsed]);

  // Sync internal isCollapsed state with isExpanded prop
  useEffect(() => {
    if (isCollapsed !== !isExpanded) {
      setIsCollapsed(!isExpanded);
    }
  }, [isExpanded]);

  const handleOpenInfo = (layer) => {
    if (layer) {
      setSelectedLayer(layer);
      setInfoModalOpen(true);
    }
  };

  const closeInfoModal = () => {
    setInfoModalOpen(false);
    setSelectedLayer(null);
  };

  const handleLayerGroupInfo = () => {
    setSelectedLayer({
      name: "Layer Groups",
      description: "Detailed information about layer groups.",
    });
    setInfoModalOpen(true);
  };

  const isLayerActive = useCallback(
    (layerId) => activeLayers.some((layer) => layer.id === layerId),
    [activeLayers]
  );

  const handleCheckboxToggle = useCallback(
    (layer) => {
      if (isLayerActive(layer.id)) {
        removeLayer(layer.id);
      } else {
        addLayer(layer);
      }
    },
    [isLayerActive, addLayer, removeLayer]
  );

  const handleAddLayer = useCallback(() => {
    setActiveTab("library");
  }, []);

  const handleToggleCollapse = () => {
    if (isCollapsed) {
      // Expanding
      setCollapseAnimation('content-expanding');
      setIsCollapsed(false);

      // Reset animation class after animation completes
      animationTimeoutRef.current = setTimeout(() => {
        setCollapseAnimation('');
      }, ANIMATION_DURATION);
    } else {
      // Collapsing
      setCollapseAnimation('content-collapsing');

      // After animation completes, actually collapse
      animationTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
        setCollapseAnimation('');
      }, ANIMATION_DURATION);
    }
  };

  const filteredLayers = layersConfig; // No search filtering since search box is removed

  const groupedLayers = groupByCategory(filteredLayers);

  const categoryOrder = ["Forest Change", "Land Cover", "Land Use", "Carbon", "Other"];

  return (
    <div className={`layer-toggle-container ${isCollapsed ? "collapsed" : ""} ${collapseAnimation}`}>
      <div className="layer-toggle-header">
        <div className="layer-toggle-title">
          <LayerGroupIcon
            ariaLabel="Layer Group Information"
            title="Layer Group Information"
            className="custom-layer-group-icon"
          />
          <h3>Thematic Layers</h3>
        </div>
        <div className="header-buttons">
          <button
            className="collapse-button"
            onClick={handleToggleCollapse}
            aria-label={isCollapsed ? "Expand Layer Toggle" : "Collapse Layer Toggle"}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
          </button>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close Layer Toggle"
            title="Close"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      {(!isCollapsed || collapseAnimation === 'content-collapsing') && (
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === "library" ? "active" : ""}`}
            onClick={() => setActiveTab("library")}
          >
            Library
          </button>
          <button
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active
          </button>
        </div>
      )}

      {(!isCollapsed || collapseAnimation === 'content-collapsing') && (
        <div className="layer-list">
          {activeTab === "active" && activeLayers.length > 0 && (
            <LayerList
              layers={activeLayers}
              onToggleVisibility={onToggleVisibility}
              onOpacityChange={onOpacityChange}
              onOpenInfo={handleOpenInfo}
              onRemove={removeLayer}
              forestLossRange={forestLossRange}
              updateYearRange={updateForestLossRange}
              updateYearSelection={updateBiomassYear}
              deforestationRange={deforestationRange}
              updateDeforestationRange={updateDeforestationRange}
              selectedYear={biomassYear}
            />
          )}
          {activeTab === "active" && activeLayers.length === 0 && (
            <p className="no-layers-message">No active layers.</p>
          )}

          {activeTab === "library" && (
            <div>
              {filteredLayers.length === 0 ? (
                <p className="no-layers-message">No layers found.</p>
              ) : (
                categoryOrder.map((cat) => {
                  if (!groupedLayers[cat] || groupedLayers[cat].length === 0) {
                    return null;
                  }
                  return (
                    <CollapsibleCategory key={cat} title={cat}>
                      {groupedLayers[cat].map((layer) => (
                        <div
                          key={layer.id}
                          className="layer-item library-layer-item"
                        >
                          <label
                            className="checkbox-container"
                            htmlFor={`checkbox-${layer.id}`}
                          >
                            <input
                              id={`checkbox-${layer.id}`}
                              type="checkbox"
                              checked={isLayerActive(layer.id)}
                              onChange={() => handleCheckboxToggle(layer)}
                              aria-label={`Toggle ${layer.name} layer`}
                            />
                            <span className="checkmark"></span>
                          </label>

                          {layer.thumbnail ? (
                            <img
                              src={layer.thumbnail}
                              alt={`${layer.name} thumbnail`}
                              className="layer-thumbnail"
                            />
                          ) : (
                            <div className="layer-thumbnail default-thumbnail">
                              <LayerGroupIcon
                                className="default-icon"
                                aria-label="Thematic Layer Icon"
                                title="Thematic Layer"
                              />
                            </div>
                          )}

                          <span className="layer-name" title={layer.name}>
                            {layer.name}
                            {layer.isNew && <span className="badge new">New</span>}
                            {layer.isUpdated && (
                              <span className="badge updated">Updated</span>
                            )}
                          </span>

                          <InfoIcon
                            onClick={() => handleOpenInfo(layer)}
                            ariaLabel={`Information about ${layer.name}`}
                            title={`Info about ${layer.name}`}
                          />
                        </div>
                      ))}
                    </CollapsibleCategory>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {(!isCollapsed || collapseAnimation === 'content-collapsing') && (
        <div className="layer-toggle-footer">
          <button
            className="reset-button"
            onClick={() => {
              if (window.confirm("Are you sure you want to reset all layers?")) {
                resetLayers();
              }
            }}
          >
            RESET LAYERS
          </button>
          <button className="add-layer-button" onClick={handleAddLayer}>
            <FaPlus className="add-icon" />
            ADD LAYER
          </button>
        </div>
      )}

      <LayerInfoModal
        isOpen={infoModalOpen}
        onRequestClose={closeInfoModal}
        layer={selectedLayer}
      />
    </div>
  );
};

LayerToggle.propTypes = {
  layersConfig: PropTypes.array.isRequired,
  activeLayers: PropTypes.array.isRequired,
  addLayer: PropTypes.func.isRequired,
  removeLayer: PropTypes.func.isRequired,
  resetLayers: PropTypes.func.isRequired,
  forestLossRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  updateForestLossRange: PropTypes.func,
  updateBiomassYear: PropTypes.func,
  onToggleVisibility: PropTypes.func.isRequired,
  onOpacityChange: PropTypes.func.isRequired,
  deforestationRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  updateDeforestationRange: PropTypes.func,
  biomassYear: PropTypes.number,
  isExpanded: PropTypes.bool.isRequired,
  onExpandChange: PropTypes.func,
};

export default LayerToggle;
